import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const CATEGORY_CONFIG = [
  { key: 'MAIN_PICK', label: 'Sistem Tercihi', summaryKey: 'systemPicks' },
  { key: 'STRONG_TREND', label: 'Guclu Model Egilimi', summaryKey: 'strongTrends' },
  { key: 'WATCH_PICK', label: 'Canli Takip', summaryKey: 'liveWatch' },
  { key: 'RISKY_PICK', label: 'Riskli Secim', summaryKey: 'risky' },
  { key: 'WEAK_DATA_SIGNAL', label: 'Veri Sinyali', summaryKey: 'dataSignals' }
];

const QUALITY_FILTERS = [
  { key: 'MAIN_PICK', label: 'Sistem Tercihi', test: (card) => String(card?.category || '').toUpperCase() === 'MAIN_PICK' },
  { key: 'HIGH_EDGE', label: 'Edge yuksek', test: (card) => Number(card?.edge || 0) >= 7 },
  { key: 'LOW_CONF', label: 'Guven dusuk', test: (card) => Number(card?.confidence || 0) < 55 },
  { key: 'CORNER', label: 'Korner fazla', test: (card) => String(card?.market || '').toLowerCase().includes('korner') },
  { key: 'REPEAT_MARKET', label: 'Ayni market tekrarleri', test: () => true },
  { key: 'GRADE_A', label: 'Kalite A', test: (card) => String(card?.qualityGrade || '').toUpperCase() === 'A' },
  { key: 'GRADE_B', label: 'Kalite B', test: (card) => String(card?.qualityGrade || '').toUpperCase() === 'B' }
];

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('tr-TR');
  } catch (error) {
    return value;
  }
};

const STATUS_DISPLAY_ORDER = ['NS', 'TBD', '1H', 'HT', '2H', 'LIVE', 'FT', 'AET', 'PEN', 'CANC', 'ABD', 'AWD', 'WO', 'UNKNOWN'];

const formatCountMap = (value = {}, preferredOrder = []) => {
  if (!value || typeof value !== 'object') return '-';

  const entries = Object.entries(value)
    .filter(([, count]) => Number(count) > 0);

  if (!entries.length) return '-';

  const orderIndex = new Map(preferredOrder.map((key, index) => [key, index]));

  return entries
    .sort(([leftKey], [rightKey]) => {
      const leftOrder = orderIndex.has(leftKey) ? orderIndex.get(leftKey) : Number.MAX_SAFE_INTEGER;
      const rightOrder = orderIndex.has(rightKey) ? orderIndex.get(rightKey) : Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return String(leftKey).localeCompare(String(rightKey), 'tr');
    })
    .map(([key, count]) => `${key}: ${count}`)
    .join(' | ');
};

const normalizeDebugError = (item, index) => {
  if (typeof item === 'string') {
    return { key: `error-${index}`, label: item, count: null };
  }

  const label = item?.label || item?.reason || item?.code || `Hata ${index + 1}`;
  const count = Number(item?.count);
  return {
    key: `${label}-${index}`,
    label,
    count: Number.isFinite(count) ? count : null
  };
};

const getStatusView = (analysisStatus, dailyRefreshStatus) => {
  const hasAnyPublishedCards = Number(
    analysisStatus?.publishedCards ||
    analysisStatus?.todayCardCount ||
    0
  ) > 0;

  if (!analysisStatus?.todayPublicationExists && !hasAnyPublishedCards) {
    return {
      label: 'Bugunku Yayin Yok',
      tone: '#c8ced8',
      background: 'rgba(108, 117, 134, 0.18)'
    };
  }

  if (String(analysisStatus?.status || dailyRefreshStatus?.status || '').trim() === 'incomplete') {
    return {
      label: 'Eksik Yayin',
      tone: '#f3c96a',
      background: 'rgba(181, 138, 26, 0.18)'
    };
  }

  const hasError = Boolean(
    dailyRefreshStatus?.status === 'failed' ||
    dailyRefreshStatus?.errorMessage ||
    dailyRefreshStatus?.matchApiPreview
  );

  if (hasError) {
    return {
      label: 'Hata',
      tone: '#ff8f8f',
      background: 'rgba(173, 42, 42, 0.18)'
    };
  }

  if (['running', 'queued', 'preparing'].includes(String(dailyRefreshStatus?.status || '').trim())) {
    return {
      label: 'Guncelleniyor',
      tone: '#f3c96a',
      background: 'rgba(181, 138, 26, 0.18)'
    };
  }

  if (Number(analysisStatus?.publishedCards || 0) > 0) {
    return {
      label: 'Yayinda',
      tone: '#7ef0b2',
      background: 'rgba(24, 126, 82, 0.18)'
    };
  }

  return {
    label: 'Guncelleniyor',
    tone: '#f3c96a',
    background: 'rgba(181, 138, 26, 0.18)'
  };
};

const AdminPanel = () => {
  const [publishedAiAnalysis, setPublishedAiAnalysis] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState(null);
  const [dailyRefreshStatus, setDailyRefreshStatus] = useState(null);
  const [dailyDebug, setDailyDebug] = useState(null);
  const [predictionPerformance, setPredictionPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [showAdvancedDebug, setShowAdvancedDebug] = useState(false);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState(null);
  const [selectedQualityFilter, setSelectedQualityFilter] = useState('MAIN_PICK');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [publishedResult, analysisStatusResult, refreshResult, debugResult, performanceResult] = await Promise.allSettled([
        api.getDailyAiCards(),
        api.getAnalysisStatus(),
        api.getDailyRefreshStatus(),
        api.getDailyDebug(),
        api.getPredictionPerformance()
      ]);

      if (publishedResult.status === 'fulfilled') {
        setPublishedAiAnalysis(publishedResult.value || null);
      } else {
        setPublishedAiAnalysis(null);
      }

      if (analysisStatusResult.status === 'fulfilled') {
        setAnalysisStatus(analysisStatusResult.value || null);
      } else {
        setAnalysisStatus(null);
      }

      if (refreshResult.status === 'fulfilled') {
        setDailyRefreshStatus(refreshResult.value || null);
      } else {
        setDailyRefreshStatus(null);
      }

      if (debugResult.status === 'fulfilled') {
        setDailyDebug(debugResult.value || null);
      } else {
        setDailyDebug(null);
      }

      if (performanceResult.status === 'fulfilled') {
        setPredictionPerformance(performanceResult.value?.performance || null);
      } else {
        setPredictionPerformance(null);
      }

      const failures = [publishedResult, analysisStatusResult, refreshResult, debugResult, performanceResult]
        .filter((result) => result.status === 'rejected')
        .map((result) => result.reason?.message)
        .filter(Boolean);

      if (failures.length) {
        setError(failures[0]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const statusView = useMemo(
    () => getStatusView(analysisStatus, dailyRefreshStatus),
    [analysisStatus, dailyRefreshStatus]
  );

  const todayPayload =
    Array.isArray(publishedAiAnalysis?.cards) && publishedAiAnalysis.cards.length > 0
      ? publishedAiAnalysis
      : null;
  const summary = todayPayload?.summary || {};
  const publishedCards = useMemo(
    () => (Array.isArray(todayPayload?.cards) ? todayPayload.cards : []),
    [todayPayload]
  );
  const todayKeyLabel = analysisStatus?.todayKey || publishedAiAnalysis?.todayKey || '-';
  const todayPublicationExists = Boolean(
    analysisStatus?.todayPublicationExists ||
    (Array.isArray(todayPayload?.cards) && todayPayload.cards.length > 0)
  );
  const selectedDateLabel = analysisStatus?.selectedDate || todayKeyLabel;
  const todayPublicationDateKey = todayPublicationExists ? (analysisStatus?.publicationDateKey || '-') : 'Bugunku yayin yok';
  const latestPublicationDateKey = analysisStatus?.latestPublicationDate || '-';
  const lastSuccessfulDateKey = analysisStatus?.lastSuccessfulDateKey || publishedAiAnalysis?.debug?.lastSuccessfulDateKey || '-';
  const todayTargetCards = Number(analysisStatus?.targetCards || 100);
  const cardCount = Number(analysisStatus?.publishedCards || 0);
  const todayCardCount = Number(analysisStatus?.todayCardCount || cardCount);
  const latestCardCount = Number(analysisStatus?.latestCardCount || 0);
  const missingCards = Number(analysisStatus?.missingCards ?? (todayPublicationExists ? Math.max(0, todayTargetCards - cardCount) : todayTargetCards));
  const todayPremiumCards = todayPayload ? Number(todayPayload?.summary?.premiumSelectionCount || 0) : 0;
  const todayValueBets = todayPayload ? Number(todayPayload?.summary?.valueBetCount || 0) : 0;
  const latestError = analysisStatus?.latestError || dailyRefreshStatus?.errorMessage || dailyDebug?.latestError || '';
  const generation = analysisStatus?.generation || {};
  const systemHealthy = !latestError && (analysisStatus?.status || dailyRefreshStatus?.status) !== 'failed';
  const selectedCategory = CATEGORY_CONFIG.find((item) => item.key === selectedCategoryKey) || null;
  const selectedCategoryCards = selectedCategory
    ? publishedCards.filter((card) => String(card?.category || '').trim().toUpperCase() === selectedCategory.key)
    : [];
  const selectedFilterConfig = QUALITY_FILTERS.find((item) => item.key === selectedQualityFilter) || QUALITY_FILTERS[0];
  const repeatedMarkets = useMemo(() => publishedCards.reduce((acc, card) => {
    const market = String(card?.market || '');
    acc[market] = (acc[market] || 0) + 1;
    return acc;
  }, {}), [publishedCards]);
  const filteredQualityCards = useMemo(() => {
    if (selectedQualityFilter === 'REPEAT_MARKET') {
      return publishedCards.filter((card) => repeatedMarkets[String(card?.market || '')] > 1);
    }
    return publishedCards.filter((card) => selectedFilterConfig.test(card));
  }, [publishedCards, repeatedMarkets, selectedFilterConfig, selectedQualityFilter]);
  const marketHitRates = Array.isArray(predictionPerformance?.marketWinRate)
    ? predictionPerformance.marketWinRate
    : Array.isArray(predictionPerformance?.marketHitRates)
      ? predictionPerformance.marketHitRates
      : [];
  const settlementCounts = useMemo(() => publishedCards.reduce((acc, card) => {
    const status = String(card?.predictionStatus || 'pending').trim().toLowerCase();
    if (status === 'won') acc.won += 1;
    else if (status === 'lost') acc.lost += 1;
    else if (status === 'void') acc.void += 1;
    else acc.pending += 1;
    return acc;
  }, {
    pending: 0,
    won: 0,
    lost: 0,
    void: 0
  }), [publishedCards]);
  const displaySettlementCounts = {
    pending: Number(analysisStatus?.pending ?? settlementCounts.pending),
    won: Number(analysisStatus?.won ?? settlementCounts.won),
    lost: Number(analysisStatus?.lost ?? settlementCounts.lost),
    void: Number(analysisStatus?.void ?? settlementCounts.void)
  };
  const statusDistributionText = useMemo(
    () => formatCountMap(dailyDebug?.statusDistribution, STATUS_DISPLAY_ORDER),
    [dailyDebug]
  );
  const rejectedReasonsText = useMemo(
    () => formatCountMap(dailyDebug?.rejectedReasons),
    [dailyDebug]
  );
  const cardGenerationErrors = useMemo(
    () => (Array.isArray(dailyDebug?.cardGenerationErrors) ? dailyDebug.cardGenerationErrors.map(normalizeDebugError) : []),
    [dailyDebug]
  );
  const fixtureSource = analysisStatus?.fixtureSource || dailyDebug?.aiFixtureSource || '-';
  const rawZeroReason = analysisStatus?.rawZeroReason || dailyDebug?.rawZeroReason || '';
  const relaxedModeUsed = Boolean(
    analysisStatus?.relaxedModeUsed ||
    dailyDebug?.relaxedModeUsed ||
    generation?.relaxedModeUsed
  );
  const lastAutoRunAt = analysisStatus?.lastAutoRunAt || null;
  const lastAutoRunStatus = analysisStatus?.lastAutoRunStatus || '-';

  const handleRegenerate = async () => {
    try {
      setError('');
      setActionMessage('Bugunun AI analiz yayini force modda uretiliyor...');
      const response = await api.runDailyAiJob(selectedDateLabel !== '-' ? selectedDateLabel : undefined, true);
      const result = response?.result || response;
      setActionMessage(
        `${result?.dateKey || result?.date || todayKeyLabel} | Raw: ${Number(result?.rawFixturesCount ?? result?.rawCount ?? 0)} | Allowed: ${Number(result?.allowedFixturesCount ?? result?.allowedCount ?? 0)} | Enriched: ${Number(result?.enrichedCount ?? 0)} | Kart: ${Number(result?.publishedCards ?? result?.cardCount ?? 0)} | Durum: ${result?.status || 'unknown'}${result?.relaxedModeUsed ? ' | Relaxed: true' : ''}${(result?.rawZeroReason || result?.cardZeroReason) ? ` | Sebep: ${result.rawZeroReason || result.cardZeroReason}` : ''}`
      );
      await loadData();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handlePublishNow = async () => {
    try {
      setError('');
      setActionMessage('AI analiz yayini force modda guncelleniyor...');
      const response = await api.runDailyAiJob(selectedDateLabel !== '-' ? selectedDateLabel : undefined, true);
      const result = response?.result || response;
      setActionMessage(
        `${result?.dateKey || result?.date || todayKeyLabel} | Raw: ${Number(result?.rawFixturesCount ?? result?.rawCount ?? 0)} | Allowed: ${Number(result?.allowedFixturesCount ?? result?.allowedCount ?? 0)} | Enriched: ${Number(result?.enrichedCount ?? 0)} | Kart: ${Number(result?.publishedCards ?? result?.cardCount ?? 0)} | Durum: ${result?.status || 'unknown'}${result?.relaxedModeUsed ? ' | Relaxed: true' : ''}${(result?.rawZeroReason || result?.cardZeroReason) ? ` | Sebep: ${result.rawZeroReason || result.cardZeroReason}` : ''}`
      );
      await loadData();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleSettlementUpdate = async () => {
    try {
      setError('');
      setActionMessage('Biten maclar sonuclandiriliyor...');
      const result = await api.settlePredictions();
      setActionMessage(result?.message || 'Tahmin sonuclari guncellendi.');
      await loadData();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleGenerateRadar = async () => {
    try {
      setError('');
      setActionMessage('Mac radari yayini arka planda baslatiliyor...');
      const result = await api.generateMatchRadarPublication();
      setActionMessage(result?.message || 'Mac radari yayini arka planda olusturuluyor.');
      await loadData();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  return (
    <div className="page analysis-page">
      <h1>Admin Operasyon Paneli</h1>

      <section className="analysis-card">
        <div className="analysis-header">
          <span>AI Analiz Yayin Merkezi</span>
          <span
            className="confidence high"
            style={{ color: statusView.tone, background: statusView.background }}
          >
            {statusView.label}
          </span>
        </div>
        <div className="all-markets">
          <div className="market-row">
            <span>Yayin Tarihi</span>
            <strong>{todayKeyLabel}</strong>
          </div>
          <div className="market-row">
            <span>Secili Tarih</span>
            <strong>{selectedDateLabel}</strong>
          </div>
          <div className="market-row">
            <span>Bugunku Yayin</span>
            <strong>{todayPublicationDateKey}</strong>
          </div>
          <div className="market-row">
            <span>Son Yayin Tarihi</span>
            <strong>{latestPublicationDateKey}</strong>
          </div>
          <div className="market-row">
            <span>Son Basarili Yayin</span>
            <strong>{lastSuccessfulDateKey}</strong>
          </div>
          <div className="market-row">
            <span>Kart Sayisi</span>
            <strong>{cardCount}</strong>
          </div>
          <div className="market-row">
            <span>Bugunku Kart Sayisi</span>
            <strong>{todayCardCount}</strong>
          </div>
          <div className="market-row">
            <span>Son Yayin Kart Sayisi</span>
            <strong>{latestCardCount}</strong>
          </div>
          <div className="market-row">
            <span>Target Cards</span>
            <strong>{todayTargetCards}</strong>
          </div>
          <div className="market-row">
            <span>Missing Cards</span>
            <strong>{missingCards}</strong>
          </div>
          <div className="market-row">
            <span>Expansion Stage</span>
            <strong>{todayPublicationExists ? (analysisStatus?.expansionStageReached || 'A') : '-'}</strong>
          </div>
          <div className="market-row">
            <span>Premium Cards</span>
            <strong>{todayPremiumCards}</strong>
          </div>
          <div className="market-row">
            <span>Value Bets</span>
            <strong>{todayValueBets}</strong>
          </div>
          <div className="market-row">
            <span>Durum</span>
            <strong style={{ color: statusView.tone }}>{statusView.label}</strong>
          </div>
        </div>
        <p className="analysis-summary">
          {todayPublicationExists
            ? `Bugunku yayin ${todayKeyLabel} icin hazir.`
            : lastSuccessfulDateKey !== '-'
              ? `Bugunku yayin yok. Son basarili yayin: ${lastSuccessfulDateKey}.`
              : 'Bugunku yayin yok.'}
        </p>
      </section>

      <section className="analysis-card">
        <div className="analysis-header">
          <span>Gunluk Uretim Hedefleri</span>
          <span className="confidence medium">{todayKeyLabel}</span>
        </div>
        <div className="all-markets">
          <div className="market-row"><span>AI Analizi</span><strong>{cardCount} / {todayTargetCards}</strong></div>
          <div className="market-row"><span>Premium Kupon</span><strong>{Number(generation.premiumCouponsPublished || 0)} / {Number(generation.premiumCouponTarget || 4)}</strong></div>
          <div className="market-row"><span>Riskli Denenebilir</span><strong>{Number(generation.riskyPublished || 0)} / {Number(generation.riskyTarget || 15)}</strong></div>
          <div className="market-row"><span>Value Bet</span><strong>{Number(generation.valueBetsPublished || 0)} / {Number(generation.valueBetTarget || 20)}</strong></div>
          <div className="market-row"><span>OpenAI</span><strong>{dailyDebug?.openAiStatus || '-'}</strong></div>
          <div className="market-row"><span>Local Fallback</span><strong>{dailyDebug?.localFallbackActive ? 'active' : 'idle'}</strong></div>
          <div className="market-row"><span>Local Model Kart</span><strong>{Number(dailyDebug?.generatedByLocalModel || 0)}</strong></div>
          <div className="market-row"><span>OpenAI Destekli Kart</span><strong>{Number(dailyDebug?.generatedByOpenAI || 0)}</strong></div>
          <div className="market-row"><span>Basarisiz Kart</span><strong>{Number(dailyDebug?.failedCards || 0)}</strong></div>
          <div className="market-row"><span>Fixture Source</span><strong>{fixtureSource}</strong></div>
          <div className="market-row"><span>Raw 0 Sebebi</span><strong>{rawZeroReason || '-'}</strong></div>
          <div className="market-row"><span>Relaxed Mode</span><strong>{relaxedModeUsed ? 'true' : 'false'}</strong></div>
          <div className="market-row"><span>Son Auto Run</span><strong>{formatDateTime(lastAutoRunAt)}</strong></div>
          <div className="market-row"><span>Auto Run Status</span><strong>{lastAutoRunStatus}</strong></div>
          <div className="market-row"><span>Son Calisan Job</span><strong>{dailyRefreshStatus?.currentStep || dailyDebug?.lastStep || '-'}</strong></div>
          <div className="market-row"><span>Son Hata</span><strong>{latestError || '-'}</strong></div>
        </div>
      </section>

      <section className="analysis-card">
        <div className="analysis-header">
          <span>Bugunun Analiz Ozeti</span>
          <span className="confidence medium">{cardCount} kart</span>
        </div>
        <div className="all-markets">
          {CATEGORY_CONFIG.map((item) => {
            const count = Number(summary[item.summaryKey] || 0);
            return (
              <button
                key={item.key}
                type="button"
                className="market-row ghost-btn"
                onClick={() => setSelectedCategoryKey(item.key)}
                style={{
                  width: '100%',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '14px',
                  marginBottom: '8px',
                  transition: 'transform 0.15s ease, border-color 0.15s ease'
                }}
              >
                <span>{item.label}</span>
                <strong>{count} {'>'}</strong>
              </button>
            );
          })}
        </div>
      </section>

      {selectedCategory ? (
        <section className="analysis-card">
          <div className="analysis-header">
            <span>{selectedCategory.label} Kartlari — {selectedCategoryCards.length}</span>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setSelectedCategoryKey(null)}
            >
              Kapat
            </button>
          </div>

          {selectedCategoryCards.length === 0 ? (
            <p className="analysis-summary">Bu kategoride kart yok.</p>
          ) : (
            <div className="analysis-grid premium-analysis-grid">
              {selectedCategoryCards.map((card, index) => (
                <article
                  key={`${selectedCategory.key}-${card.matchId || index}-${card.market || 'market'}`}
                  className="analysis-card"
                >
                  <div className="analysis-header">
                    <span>{card.league?.name || '-'}</span>
                    <span className="confidence medium">{card.category || selectedCategory.label}</span>
                  </div>
                  <h3>{card.homeTeam?.name || '-'} - {card.awayTeam?.name || '-'}</h3>
                  <div className="all-markets">
                    <div className="market-row"><span>Mac saati</span><strong>{formatDateTime(card.matchDate)}</strong></div>
                    <div className="market-row"><span>Market</span><strong>{card.market || '-'}</strong></div>
                    <div className="market-row"><span>Model</span><strong>%{card.modelProbability || 0}</strong></div>
                    <div className="market-row"><span>Edge</span><strong>{card.edge ?? 0}</strong></div>
                    <div className="market-row"><span>Guven</span><strong>%{card.confidence || 0}</strong></div>
                    <div className="market-row"><span>Kategori</span><strong>{card.level || selectedCategory.label}</strong></div>
                  </div>
                  <p className="analysis-summary">{card.aiInsight || 'Kisa AI yorumu bulunmuyor.'}</p>
                  <div className="market-row">
                    <button type="button" className="ghost-btn">Karti Yayindan Kaldir</button>
                    <button type="button" className="ghost-btn">Kategoriyi Degistir</button>
                    <button type="button" className="ghost-btn">Detaylari Gor</button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      ) : null}

      <section className="analysis-card">
        <div className="analysis-header">
          <span>Hizli Aksiyonlar</span>
          <span className="confidence high">Arka Plan Job</span>
        </div>
          <div className="market-row">
            <button className="btn-primary" type="button" onClick={handleRegenerate}>
              Bugunun Analizini Yeniden Uret
            </button>
            <button className="btn-primary" type="button" onClick={handlePublishNow}>
              Yayini Hemen Guncelle
            </button>
            <button className="btn-primary" type="button" onClick={handleSettlementUpdate}>
              Biten Maclari Sonuclandir
            </button>
          </div>
        {Number(dailyDebug?.radarPublicationCount || 0) === 0 ? (
          <div className="market-row" style={{ marginTop: '12px' }}>
            <button className="btn-primary" type="button" onClick={handleGenerateRadar}>
              Radar Yayini Yok, Manuel Uret
            </button>
          </div>
        ) : null}
        {actionMessage ? <p className="analysis-summary">{actionMessage}</p> : null}
      </section>

      <section className="analysis-card">
        <div className="analysis-header">
          <span>Admin Kalite Kontrol</span>
          <span className="confidence medium">{filteredQualityCards.length} kart</span>
        </div>
        <div className="all-markets">
          {QUALITY_FILTERS.map((filterItem) => (
            <button
              key={filterItem.key}
              type="button"
              className="ghost-btn"
              onClick={() => setSelectedQualityFilter(filterItem.key)}
              style={{
                border: selectedQualityFilter === filterItem.key ? '1px solid rgba(255, 191, 71, 0.55)' : '1px solid rgba(255,255,255,0.08)',
                borderRadius: '999px',
                padding: '10px 14px',
                marginRight: '8px',
                marginBottom: '8px'
              }}
            >
              {filterItem.label}
            </button>
          ))}
        </div>
        {filteredQualityCards.length === 0 ? (
          <p className="analysis-summary">Bu filtrede kart bulunmuyor.</p>
        ) : (
          <div className="analysis-grid premium-analysis-grid">
            {filteredQualityCards.slice(0, 8).map((card, index) => (
              <article key={`${selectedQualityFilter}-${card.matchId || index}-${card.market || 'market'}`} className="analysis-card">
                <div className="analysis-header">
                  <span>{card.homeTeam?.name || '-'} - {card.awayTeam?.name || '-'}</span>
                  <span className="confidence medium">{card.qualityGrade || 'C'}</span>
                </div>
                <div className="all-markets">
                  <div className="market-row"><span>Market</span><strong>{card.market || '-'}</strong></div>
                  <div className="market-row"><span>Model</span><strong>%{card.modelProbability || 0}</strong></div>
                  <div className="market-row"><span>Guven</span><strong>%{card.confidence || 0}</strong></div>
                  <div className="market-row"><span>Edge</span><strong>{card.edgeLabel || (card.edge ?? '-')}</strong></div>
                  <div className="market-row"><span>edgeScore</span><strong>{card.scoreBreakdown?.edgeScore ?? 0}</strong></div>
                  <div className="market-row"><span>confidenceScore</span><strong>{card.scoreBreakdown?.confidenceScore ?? 0}</strong></div>
                  <div className="market-row"><span>dataQualityScore</span><strong>{card.scoreBreakdown?.dataQualityScore ?? 0}</strong></div>
                  <div className="market-row"><span>marketReliability</span><strong>{card.scoreBreakdown?.marketReliability ?? 0}</strong></div>
                  <div className="market-row"><span>riskPenalty</span><strong>{card.scoreBreakdown?.riskPenalty ?? 0}</strong></div>
                  <div className="market-row"><span>finalScore</span><strong>{card.scoreBreakdown?.finalScore ?? card.finalPickScore ?? 0}</strong></div>
                </div>
                <p className="analysis-summary">{card.evidence || card.aiInsight || '-'}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="analysis-card">
        <div className="analysis-header">
          <span>Gecmis Performans</span>
          <span className="confidence medium">{predictionPerformance?.totalSettledPredictions || 0} sonuc</span>
        </div>
        <div className="all-markets">
          <div className="market-row"><span>Toplam Tahmin</span><strong>{predictionPerformance?.totalPredictions || 0}</strong></div>
          <div className="market-row"><span>Tutan</span><strong>{predictionPerformance?.won || 0}</strong></div>
          <div className="market-row"><span>Tutmayan</span><strong>{predictionPerformance?.lost || 0}</strong></div>
          <div className="market-row"><span>Void</span><strong>{predictionPerformance?.void || 0}</strong></div>
          <div className="market-row"><span>Genel Win Rate</span><strong>%{predictionPerformance?.winRate || 0}</strong></div>
          <div className="market-row"><span>Son 7 Gun Isabet</span><strong>%{predictionPerformance?.last7DaysHitRate || 0}</strong></div>
          <div className="market-row"><span>Son 30 Gun Isabet</span><strong>%{predictionPerformance?.last30DaysHitRate || 0}</strong></div>
          <div className="market-row"><span>A Kalite Win Rate</span><strong>%{predictionPerformance?.gradeWinRate?.A || 0}</strong></div>
          <div className="market-row"><span>B Kalite Win Rate</span><strong>%{predictionPerformance?.gradeWinRate?.B || 0}</strong></div>
        </div>
        {marketHitRates.length ? (
          <div className="all-markets" style={{ marginTop: '10px' }}>
            {marketHitRates.slice(0, 6).map((item) => (
              <div key={item.market || item.label} className="market-row">
                <span>{item.market || item.label}</span>
                <strong>%{item.winRate ?? item.hitRate ?? 0}</strong>
              </div>
            ))}
          </div>
        ) : (
          <p className="analysis-summary">Henuz yeterli sonuc verisi yok.</p>
        )}
      </section>

      <section className="analysis-card">
        <div className="analysis-header">
          <span>Tahmin Sonuclari</span>
          <span className="confidence medium">{publishedCards.length} kart</span>
        </div>
        <div className="all-markets">
          <div className="market-row"><span>Bekleyen Tahmin</span><strong>{displaySettlementCounts.pending}</strong></div>
          <div className="market-row"><span>Tutan Tahmin</span><strong>{displaySettlementCounts.won}</strong></div>
          <div className="market-row"><span>Tutmayan Tahmin</span><strong>{displaySettlementCounts.lost}</strong></div>
          <div className="market-row"><span>Gecersiz Tahmin</span><strong>{displaySettlementCounts.void}</strong></div>
          <div className="market-row"><span>Last Settlement Run</span><strong>{formatDateTime(analysisStatus?.lastSettlementRun)}</strong></div>
        </div>
      </section>

      <section className="analysis-card">
        <div className="analysis-header">
          <span>Sistem Durumu</span>
          <span className="confidence high" style={{ color: systemHealthy ? '#7ef0b2' : '#ff8f8f' }}>
            {systemHealthy ? 'Normal Calisiyor' : 'Veri Saglayici Hatasi'}
          </span>
        </div>
        <p className="analysis-summary">
          Durum: {systemHealthy ? 'Normal Calisiyor' : 'Veri Saglayici Hatasi'}
        </p>
        {!systemHealthy && latestError ? (
          <p className="analysis-summary">Son Hata: {latestError}</p>
        ) : null}
      </section>

      <section className="analysis-card">
        <div className="analysis-header">
          <span>Otomatik Guncelleme</span>
          <span className="confidence medium">Europe/Istanbul</span>
        </div>
        <div className="all-markets">
          <div className="market-row"><span>00:30</span><strong>Yeni Gun Hazirlik</strong></div>
                  <div className="market-row">
            <span>Sonraki Guncelleme</span>
              <strong>{formatDateTime(analysisStatus?.nextRun || dailyRefreshStatus?.nextScheduledRun)}</strong>
            </div>
          </div>
        </section>

      <section className="analysis-card">
        <div className="analysis-header">
          <span>Gelismis Debug</span>
          <button
            type="button"
            className="ghost-btn"
            onClick={() => setShowAdvancedDebug((current) => !current)}
          >
            {showAdvancedDebug ? 'Gelismis Debug Kapat' : 'Gelismis Debug Ac'}
          </button>
        </div>

        {showAdvancedDebug ? (
          <div className="all-markets">
            <div className="market-row"><span>Football Total Matches</span><strong>{dailyDebug?.footballTotalMatches || 0}</strong></div>
            <div className="market-row"><span>Raw Fixtures</span><strong>{dailyDebug?.rawFixturesCount || 0}</strong></div>
            <div className="market-row"><span>AI Fixture Source</span><strong>{dailyDebug?.aiFixtureSource || '-'}</strong></div>
            <div className="market-row"><span>Requested Date</span><strong>{dailyDebug?.requestedDate || todayKeyLabel}</strong></div>
            <div className="market-row"><span>Provider Response Count</span><strong>{dailyDebug?.providerResponseCount || 0}</strong></div>
            <div className="market-row"><span>DB Fixture Count</span><strong>{dailyDebug?.dbFixtureCount || 0}</strong></div>
            <div className="market-row"><span>Cache Fixture Count</span><strong>{dailyDebug?.cacheFixtureCount || 0}</strong></div>
            <div className="market-row"><span>Fallback Source Used</span><strong>{dailyDebug?.fallbackSourceUsed || '-'}</strong></div>
            <div className="market-row"><span>Status Dagilimi</span><strong>{statusDistributionText}</strong></div>
            <div className="market-row"><span>Finished Diye Elenen</span><strong>{dailyDebug?.finishedRejectedCount || 0}</strong></div>
            <div className="market-row"><span>Baslamamis Ama Elenen</span><strong>{dailyDebug?.unfinishedRejectedCount || 0}</strong></div>
            <div className="market-row"><span>Allowed (Ilk)</span><strong>{dailyDebug?.initialAllowedCount || 0}</strong></div>
            <div className="market-row"><span>Allowed (Son)</span><strong>{dailyDebug?.allowedFixturesCount || dailyDebug?.afterAllowlistCount || 0}</strong></div>
            <div className="market-row"><span>Relaxed Mode</span><strong>{dailyDebug?.relaxedModeUsed ? 'true' : 'false'}</strong></div>
            <div className="market-row"><span>Forced Fallback</span><strong>{dailyDebug?.forcedAllowedFallbackUsed ? 'true' : 'false'}</strong></div>
            <div className="market-row"><span>Fallback Sebebi</span><strong>{dailyDebug?.forcedAllowedFallbackReason || '-'}</strong></div>
            <div className="market-row"><span>OpenAI Durumu</span><strong>{dailyDebug?.openAiStatus || '-'}</strong></div>
            <div className="market-row"><span>Quota Fail Kart</span><strong>{dailyDebug?.openAiQuotaExceededCount || 0}</strong></div>
            <div className="market-row"><span>Local Model Uretimi</span><strong>{dailyDebug?.generatedByLocalModel || 0}</strong></div>
            <div className="market-row"><span>OpenAI Destegi</span><strong>{dailyDebug?.generatedByOpenAI || 0}</strong></div>
            <div className="market-row"><span>Basarisiz Kart</span><strong>{dailyDebug?.failedCards || 0}</strong></div>
            <div className="market-row"><span>Raw Leagues</span><strong>{dailyDebug?.rawLeagueCount || 0}</strong></div>
            <div className="market-row"><span>Allowed Leagues Loaded</span><strong>{dailyDebug?.allowedLeagueCount || 0}</strong></div>
            <div className="market-row"><span>After Allowlist</span><strong>{dailyDebug?.afterAllowlistCount || 0}</strong></div>
            <div className="market-row"><span>After Blocklist</span><strong>{dailyDebug?.afterBlocklistCount || 0}</strong></div>
            <div className="market-row"><span>After Data Quality</span><strong>{dailyDebug?.afterDataQualityCount || 0}</strong></div>
            <div className="market-row"><span>Published Cards</span><strong>{dailyDebug?.publishedCount || 0}</strong></div>
            <div className="market-row"><span>Used Fallback Allowlist</span><strong>{dailyDebug?.usedFallbackAllowlist ? 'true' : 'false'}</strong></div>
            <div className="market-row"><span>Rejected Reasons</span><strong>{rejectedReasonsText}</strong></div>
            <div className="market-row"><span>Total Matches Fetched</span><strong>{dailyRefreshStatus?.totalMatchesFetched || 0}</strong></div>
            <div className="market-row"><span>Eligible Matches</span><strong>{dailyRefreshStatus?.eligibleMatches || 0}</strong></div>
            <div className="market-row"><span>Candidates Generated</span><strong>{dailyRefreshStatus?.candidatesGenerated || 0}</strong></div>
            <div className="market-row"><span>Cards Saved</span><strong>{dailyRefreshStatus?.cardsSaved || 0}</strong></div>
            <div className="market-row"><span>Last Step</span><strong>{dailyRefreshStatus?.currentStep || dailyDebug?.lastStep || '-'}</strong></div>
            <div className="market-row"><span>Latest Error</span><strong>{latestError || '-'}</strong></div>
            <div className="market-row"><span>Match API Status</span><strong>{dailyRefreshStatus?.matchApiStatus || 0}</strong></div>
            <div className="market-row"><span>Match API URL</span><strong>{dailyRefreshStatus?.matchApiUrl || '-'}</strong></div>
            <div className="market-row"><span>Content-Type</span><strong>{dailyRefreshStatus?.matchApiContentType || '-'}</strong></div>
            <div className="market-row"><span>Preview</span><strong>{dailyRefreshStatus?.matchApiPreview || '-'}</strong></div>
            <div className="market-row"><span>Dusuk Veri Mac</span><strong>{dailyDebug?.lowDataMatches || 0}</strong></div>
            <div className="market-row"><span>Mapping Eksik Mac</span><strong>{dailyDebug?.mappingMissingMatches || 0}</strong></div>
            <div className="market-row"><span>Premium Disi Birakilan</span><strong>{dailyDebug?.premiumExcludedMatches || 0}</strong></div>
            <div className="market-row"><span>API-Football Veri Gelen</span><strong>{dailyDebug?.apiFootballDataMatches || 0}</strong></div>
            <div className="market-row"><span>Fallback Veri Gelen</span><strong>{dailyDebug?.fallbackProviderDataMatches || 0}</strong></div>
            <div className="market-row"><span>Veri Bulunamayan</span><strong>{dailyDebug?.noDataMatches || 0}</strong></div>
            {Number(dailyDebug?.rawFixturesCount || 0) > 50 &&
            Number(dailyDebug?.initialAllowedCount || 0) === 0 &&
            dailyDebug?.forcedAllowedFallbackUsed ? (
              <p className="analysis-summary" style={{ marginTop: '12px' }}>
                Raw 50 ustunde olmasina ragmen allowed sifira dustu. Sistem forced fallback ile temel fixture bilgisi olan maclari yeniden allowed havuzuna aldi.
              </p>
            ) : null}
            {cardGenerationErrors.length > 0 ? (
              <div className="analysis-summary" style={{ marginTop: '12px' }}>
                Kart Uretim Hatalari:
                {cardGenerationErrors.slice(0, 10).map((item) => (
                  <div key={item.key} className="market-row">
                    <span>{item.label}</span>
                    <strong>{item.count === null ? '-' : item.count}</strong>
                  </div>
                ))}
              </div>
            ) : null}
            {Array.isArray(dailyDebug?.rejectedSample) && dailyDebug.rejectedSample.length > 0 ? (
              <div className="analysis-summary" style={{ marginTop: '12px' }}>
                Elenen Ornek Maclar:
                {dailyDebug.rejectedSample.slice(0, 20).map((item, index) => (
                  <div key={`${item.leagueId || item.league || 'league'}-${index}`} className="market-row">
                    <span>{item.country || '-'} | {item.league || '-'} | {item.homeTeam || '-'} - {item.awayTeam || '-'}</span>
                    <strong>{item.reason || '-'}</strong>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <p className="analysis-summary">Teknik metrikler varsayilan olarak gizlidir.</p>
        )}
      </section>

      {loading ? <div className="loading">Admin paneli yukleniyor...</div> : null}
      {error ? <div className="error">{error}</div> : null}
    </div>
  );
};

export default AdminPanel;
